import { CHAT_INTENT } from "./chatStates";

const EXACT_ALIASES = new Map([
  ["home", CHAT_INTENT.HOME],
  ["menu", CHAT_INTENT.HOME],
  ["start over", CHAT_INTENT.HOME],
  ["back", CHAT_INTENT.BACK],
  ["go back", CHAT_INTENT.BACK],
  ["cancel", CHAT_INTENT.CANCEL],
  ["stop", CHAT_INTENT.CANCEL],
  ["never mind", CHAT_INTENT.CANCEL],
  ["nevermind", CHAT_INTENT.CANCEL],
  ["help", CHAT_INTENT.HELP],
  ["support", CHAT_INTENT.SUPPORT],
  ["recipients", CHAT_INTENT.RECIPIENTS],
  ["recipient", CHAT_INTENT.RECIPIENTS],
  ["beneficiaries", CHAT_INTENT.RECIPIENTS],
  ["activity", CHAT_INTENT.ACTIVITY],
  ["history", CHAT_INTENT.ACTIVITY],
  ["transactions", CHAT_INTENT.ACTIVITY],
  ["transfers", CHAT_INTENT.ACTIVITY],
  ["rates", CHAT_INTENT.RATES],
  ["exchange rate", CHAT_INTENT.RATES],
  ["fx", CHAT_INTENT.RATES],
  ["profile", CHAT_INTENT.PROFILE],
  ["my account", CHAT_INTENT.PROFILE],
  ["security", CHAT_INTENT.SECURITY],
  ["pin", CHAT_INTENT.SECURITY],
  ["verification", CHAT_INTENT.KYC],
  ["kyc", CHAT_INTENT.KYC],
  ["verify identity", CHAT_INTENT.KYC],
  ["notifications", CHAT_INTENT.NOTIFICATIONS],

  // French: the chat UI follows the customer's locale, and the parser is the
  // fallback whenever the model is unavailable.
  ["accueil", CHAT_INTENT.HOME],
  ["menu principal", CHAT_INTENT.HOME],
  ["recommencer", CHAT_INTENT.HOME],
  ["retour", CHAT_INTENT.BACK],
  ["annuler", CHAT_INTENT.CANCEL],
  ["arrêter", CHAT_INTENT.CANCEL],
  ["laisse tomber", CHAT_INTENT.CANCEL],
  ["aide", CHAT_INTENT.HELP],
  ["assistance", CHAT_INTENT.SUPPORT],
  ["bénéficiaires", CHAT_INTENT.RECIPIENTS],
  ["bénéficiaire", CHAT_INTENT.RECIPIENTS],
  ["activité", CHAT_INTENT.ACTIVITY],
  ["historique", CHAT_INTENT.ACTIVITY],
  ["transferts", CHAT_INTENT.ACTIVITY],
  ["taux", CHAT_INTENT.RATES],
  ["taux de change", CHAT_INTENT.RATES],
  ["profil", CHAT_INTENT.PROFILE],
  ["mon compte", CHAT_INTENT.PROFILE],
  ["sécurité", CHAT_INTENT.SECURITY],
  ["code pin", CHAT_INTENT.SECURITY],
  ["vérification", CHAT_INTENT.KYC],
  ["vérifier mon identité", CHAT_INTENT.KYC],
]);

export const SEND_CURRENCIES = Object.freeze(["GBP", "USD", "EUR"]);

const SEND_PATTERNS = [
  /^(?:send|pay|transfer)\b/i,
  /\b(?:send|pay|transfer)\s+(?:money|funds)\b/i,
  /^(?:envoyer|envoie|envoyez|payer|paie|payez|transférer|virer)(?:\s|$)/i,
  /(?:^|\s)(?:envoyer|transférer)\s+(?:de\s+l['’]argent|des\s+fonds)(?:\s|$)/i,
];

const ACTIVITY_PATTERNS = [
  /\b(show|view)\b.*\b(transfers|transactions|activity|history)\b/i,
  /\b(my|recent)\b.*\b(transfers|transactions)\b/i,
  /(?:^|\s)(?:voir|afficher|mes)(?:\s|$).*(?:transferts|transactions|activité|historique)/i,
];

const RATE_PATTERNS = [
  /\b(exchange|fx)\s+rate\b/i,
  /(?:^|\s)taux\s+de\s+change(?:\s|$)/i,
  /\b(convert|conversion)\b/i,
  /^\s*[£$€]?\s*\d+(?:\.\d+)?\s+(?:gbp|usd|eur)\s+(?:to|in)\s+[a-z]{3}\s*$/i,
];

export function normalizeChatInput(value = "") {
  return String(value).trim().replace(/\s+/g, " ");
}

export function parseMoneyExpression(value = "") {
  const text = normalizeChatInput(value);
  const match = text.match(/(?:^|\s)(£|\$|€)?\s*(\d+(?:\.\d{1,2})?)\s*(GBP|USD|EUR)?\b/i);

  if (!match) return null;

  const symbolCurrency = {
    "£": "GBP",
    "$": "USD",
    "€": "EUR",
  }[match[1]];

  const amount = Number(match[2]);
  if (!Number.isFinite(amount) || amount <= 0) return null;

  return {
    amount,
    currency: (match[3] || symbolCurrency || null)?.toUpperCase() ?? null,
  };
}

export function parseSendRequest(value = "") {
  const text = normalizeChatInput(value);
  if (!SEND_PATTERNS.some((pattern) => pattern.test(text))) return null;

  const money = parseMoneyExpression(text);
  const toMatch = text.match(/(?:^|\s)(?:to|à)\s+(.+?)(?:\s+(?:in|au|en)\s+[a-zà-ÿ\s]+)?$/i);

  return {
    amount: money?.amount ?? null,
    currency: money?.currency ?? null,
    recipientQuery: toMatch?.[1]?.trim() || null,
  };
}

export function parseTopLevelIntent(value = "") {
  const text = normalizeChatInput(value);
  const normalized = text.toLowerCase();

  if (!text) return { intent: CHAT_INTENT.UNKNOWN };
  if (EXACT_ALIASES.has(normalized)) {
    return { intent: EXACT_ALIASES.get(normalized) };
  }

  const send = parseSendRequest(text);
  if (send) return { intent: CHAT_INTENT.SEND_MONEY, ...send };
  if (ACTIVITY_PATTERNS.some((pattern) => pattern.test(text))) {
    return { intent: CHAT_INTENT.ACTIVITY };
  }
  if (RATE_PATTERNS.some((pattern) => pattern.test(text))) {
    return { intent: CHAT_INTENT.RATES };
  }

  return { intent: CHAT_INTENT.UNKNOWN };
}

export function parsePositiveAmount(value = "") {
  const money = parseMoneyExpression(value);
  if (money) return money;

  const number = Number(normalizeChatInput(value));
  if (!Number.isFinite(number) || number <= 0) return null;
  return { amount: number, currency: null };
}
